import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { UserSelectionType } from 'webapp/enums/enums';

import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectComponent from 'webapp/components/form/select';

import type Alert from 'webapp/models-custom/alert';
import type Scorecard from 'webapp/models-custom/scorecard';

interface Args {
	model: Scorecard | Alert;
	submitInProgress: boolean;
	theme?: string;
}

export default class CommonRecipientsSelectorComponent extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked radiobuttonSelection: UserSelectionType;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const recipientCount = this.model.userIds.length,
			isMoreThanMe = recipientCount && (recipientCount > 1 || !this.isUserSubscriber);

		this.radiobuttonSelection = isMoreThanMe
			? UserSelectionType.ME_AND_SELECTED
			: UserSelectionType.ME;
	}

	get model() {
		return this.args.model;
	}

	get showRecipients() {
		return this.radiobuttonSelection === UserSelectionType.ME_AND_SELECTED;
	}

	get usersList() {
		return this.mmUsersService.activatedUsersList
			.filter((user) => {
				return user.value !== this.currentUserService.id;
			})
			.map((user) => {
				return {
					value: user.value,
					label: user.label,
					label2: user.label2,
				};
			});
	}

	get userSelectionValues() {
		return [
			{ value: UserSelectionType.ME, label: 'Only me' },
			{
				value: UserSelectionType.ME_AND_SELECTED,
				label: (this.model.isActive ? 'Me and s' : 'S') + 'elected users',
			},
		];
	}

	get userIdsStringified() {
		// for avoiding the bug https://app.asana.com/0/188486158010832/1199684179035126/f
		return this.model.userIds.map((i) => String(i));
	}

	get isUserSubscriber() {
		const userId = this.currentUserService.numericId,
			userIds = this.model.userIds;
		return !userIds.length || (userId && userIds.includes(userId));
	}

	@action
	changeRecipients(userIds: string[]) {
		const userIdsNumeric = userIds.map((i) => Number(i));
		this.model.userIds = userIdsNumeric;
	}

	@action
	onRecipientsTypeChange(value: AnySimpleValue) {
		const selected = value as UserSelectionType;
		if (selected === UserSelectionType.ME) {
			this.model.userIds = [];
		}
		this.radiobuttonSelection = selected;
	}

	@action
	selectAll() {
		this.model.userIds = this.usersList.map((user) => {
			return +user.value;
		});
	}
	<template>
		<div class="recipients-selector">
			<FormRadiobuttonGroupComponent
				class="radiobutton-default"
				@name="recipientsType"
				@selected={{this.radiobuttonSelection}}
				@options={{this.userSelectionValues}}
				@isDisabled={{@submitInProgress}}
				@onChange={{this.onRecipientsTypeChange}}
			/>

			{{#if this.showRecipients}}
				<a role="button" {{on "click" this.selectAll}} class="select-all">Select all</a>
				<div class="recipients">
					<FormSelectComponent
						name="users"
						@selected={{this.userIdsStringified}}
						@options={{this.usersList}}
						@onChange={{this.changeRecipients}}
						@placeholder="Recipients"
						@isNumeric={{false}}
						@isMultiple={{true}}
						@showFloatlabel={{false}}
						@isDisabled={{@submitInProgress}}
						@theme={{@theme}}
					/>
				</div>
			{{/if}}
		</div>
	</template>
}
